import { useCallback, useState } from 'react';
import { useHandleApiResponse, useToast } from '../../hooks';
import { useSendCallFeedbackMutation } from '../../services/mutations';
import { Alignment, ComponentSize } from '../../types';
import { AlertType, ButtonColor, ButtonVariant, Icon, IconButton, Typography, TypographyType } from '../shared';

const FEEDBACK_ERROR_MSG = 'Failed to send call feedback. Please try again.';

interface DialerFeedbackModalProps {
  callSid: string;
}

const DialerFeedbackModal = ({ callSid }: DialerFeedbackModalProps) => {
  const [isFeedbackSent, setIsFeedbackSent] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  const [sendCallFeedback, { isLoading }] = useSendCallFeedbackMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  const onSendFeedback = useCallback(
    async (isSatisfied: boolean) => {
      try {
        const response = await sendCallFeedback({
          callSid: callSid,
          isSatisfied,
        });

        handleApiResponse({
          response,
          errorMsg: FEEDBACK_ERROR_MSG,
          hideSuccessToast: true, // Hide the success toast because we already show a success state.
          onSuccess: () => setIsFeedbackSent(true),
        });
      } catch (error) {
        console.error(error);
        showToast({ message: FEEDBACK_ERROR_MSG, type: AlertType.ERROR });
      }
    },
    [callSid, sendCallFeedback, handleApiResponse, showToast]
  );

  if (!isOpen) return null;

  return (
    <div className="absolute left-1/2 top-1/2 flex h-36 w-72 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl bg-base-100 p-8 shadow-dialog">
      {isFeedbackSent && (
        <div className="absolute right-2 top-2">
          <IconButton
            icon={Icon.CLOSE}
            onClick={() => setIsOpen(false)}
            size={ComponentSize.X_SMALL}
            color={ButtonColor.NEUTRAL}
            variant={ButtonVariant.GHOST}
          />
        </div>
      )}
      <div className="flex flex-col items-center gap-4">
        <Typography alignment={Alignment.CENTER} type={TypographyType.H3}>
          {isFeedbackSent ? 'Thank you for your feedback!' : 'Did your call feel real?'}
        </Typography>
        {!isFeedbackSent && (
          <div className="flex items-center gap-2">
            <IconButton
              disabled={isLoading}
              icon={Icon.THUMBS_UP}
              variant={ButtonVariant.SOFT}
              onClick={() => onSendFeedback(true)}
            />
            <IconButton
              disabled={isLoading}
              color={ButtonColor.DESTRUCTIVE}
              icon={Icon.THUMBS_DOWN}
              variant={ButtonVariant.SOFT}
              onClick={() => onSendFeedback(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DialerFeedbackModal;
